import * as React from 'react';
import { Navbar, NavbarBrand, UncontrolledTooltip } from 'reactstrap';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { bindActionCreators, Dispatch } from 'redux';
import { ApplicationState } from '../../store';
import * as ConsoleStore from '../../store/Console';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Application } from '../../store/Console';
import { getUID } from '../../utils';
import './ConsoleNavMenu.css';
import { faAngleDoubleLeft, faAngleDoubleRight } from '@fortawesome/pro-light-svg-icons';
import { faAngleRight, faAngleLeft } from '@fortawesome/free-solid-svg-icons';

type ConsoleSidebarProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToPros> & RouteComponentProps;

interface ConsoleSidebarState {
    expandedItems: { [key: string]: boolean };
    selectedAppName?: string;
    selectedSubmenuRoute?: string;
    collapsed: boolean;
}
class ConsoleSidebar extends React.Component<ConsoleSidebarProps, ConsoleSidebarState> {
    constructor(props: ConsoleSidebarProps) {
        super(props);
        const savedCollapsed = sessionStorage.getItem('consoleSidebarCollapsed');
        const isCollapsed = savedCollapsed === 'true';

        this.state = {
            expandedItems: {},
            collapsed: isCollapsed
        };
        this.props.consoleActions.getApplications();
    }

    componentDidMount() {
        const savedExpanded = sessionStorage.getItem('consoleSidebarExpanded');
        let expandedItems: { [key: string]: boolean } = {};
        try {
            expandedItems = savedExpanded ? JSON.parse(savedExpanded) : {};
        } catch {
            expandedItems = {};
        }

        this.setState({ expandedItems }, () => this.syncWithPath());
    }

    componentDidUpdate(prevProps: ConsoleSidebarProps) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            this.syncWithPath();
        }
    }

    private syncWithPath() {
        const path = this.props.location.pathname;

        const submenuRouteMap: Record<string, string> = {
            '/console': 'Home',
            '/admin/user_management': 'GDS Portal',
            '/admin/management/portal-settings/company': 'GDS Portal',
            '/admin/role-management': 'GDS Portal',
            '/admin/dashboard-management': 'GDS Portal',
            '/data-intelligence/policy-monitoring': 'Policy Monitoring',
        };

        const matchedEntry = Object.entries(submenuRouteMap).find(([route]) => path.startsWith(route));

        if (matchedEntry) {
            const [matchedSubmenu, appName] = matchedEntry;

            this.setState(prevState => {
                const updatedItems = {
                    ...prevState.expandedItems,
                    [appName]: true
                };
                sessionStorage.setItem('consoleSidebarExpanded', JSON.stringify(updatedItems));
                return {
                    selectedAppName: appName,
                    selectedSubmenuRoute: matchedSubmenu,
                    expandedItems: updatedItems
                };
            });
        } else {
            this.setState({
                selectedAppName: undefined,
                selectedSubmenuRoute: undefined,
            });
        }
    }

    private onLaunch = (event: React.MouseEvent, object: Application) => {
        event.preventDefault();
        this.props.consoleActions.launchApplication(object);
    }

    private toggleExpand = (key: string) => {
        this.setState(prevState => {
            const updatedItems = {
                ...prevState.expandedItems,
                [key]: !prevState.expandedItems[key]
            };

            const isExpanding = !prevState.expandedItems[key];
            const isCollapsed = isExpanding ? false : prevState.collapsed;
            sessionStorage.setItem('consoleSidebarExpanded', JSON.stringify(updatedItems));
            sessionStorage.setItem('consoleSidebarCollapsed', JSON.stringify(isCollapsed));

            return {
                expandedItems: updatedItems,
                collapsed: isCollapsed
            };
        });
    }

    private toggleCollapse = () => {
        this.setState(prevState => {
            const isCollapsing = !prevState.collapsed;
            const updatedItems = isCollapsing ? {} : prevState.expandedItems;

            sessionStorage.setItem('consoleSidebarCollapsed', JSON.stringify(isCollapsing));
            sessionStorage.setItem('consoleSidebarExpanded', JSON.stringify(updatedItems));

            return {
                collapsed: isCollapsing,
                expandedItems: updatedItems
            };
        });
    }

    public render() {
        const { collapsed } = this.state;
        const applications = this.props.console?.applicationsState.applications ?? [];

        // Group applications by application.group
        const groupedApplications = applications.reduce((acc, app) => {
            if (!app.display) return acc;
            const group = app.group;
            const key = group || app.applicationFullName + getUID(); // Use app itself as the key if no group
            if (!acc[key]) {
                acc[key] = { groupName: group ?? null, apps: [] };
            }
            acc[key].apps.push(app);
            return acc;
        }, {} as Record<string, { groupName: string | null, apps: typeof applications }>);

        return (
            <React.Fragment>
                <div
                    className={`console-sidebar bg-gds ${collapsed ? 'collapsed' : ''}`}
                    style={{ width: collapsed ? '50px' : '250px' }}
                >
                    <section>
                        <Navbar className="navbar-expand-sm border-bottom box-shadow navbar-dark bg-dark bg-gds console-navbar mb-0" light style={{ height: "50px" }}>
                            <NavbarBrand tag={Link} to="/">
                                {this.state.collapsed ? (
                                    <img src="resources/gds_logo_new.png" className="collapsed-logo" alt="Mini Logo" />
                                ) : (
                                    <img src="resources/logowhite.png" className="sidebar-logo" alt="Full Logo" />
                                )}
                            </NavbarBrand>
                        </Navbar>

                        <div className="text-end mb-0">
                        <button
                            className="sidebar-toggle-button"
                            onClick={this.toggleCollapse}
                            title={this.state.collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                        >
                            <span className="arrow-icon">
                                <FontAwesomeIcon icon={this.state.collapsed ? faAngleRight : faAngleLeft} />
                            </span>
                        </button>
                        </div>

                        {this.props.console?.applicationsState.isLoading ? (
                            <section className="text-center loading-indicator h-100 d-flex justify-content-center align-items-center p-2">
                                <FontAwesomeIcon icon={faSpinner} size="1x" spin={true} />
                            </section>
                        ) : (
                            <>
                                {Object.entries(groupedApplications).map(([key, { groupName, apps }], index) => {
                                    const isExpanded = this.state.expandedItems[groupName ?? key];
                                    const groupIcon = apps[0]?.groupIcon;
                                    const groupId = `group-${index}`;
                                    return (
                                        <div key={key}>
                                            {groupName && (
                                                <button
                                                    id={groupId}
                                                    className="btn app-link"
                                                    onClick={() => this.toggleExpand(groupName)}
                                                >
                                                    <div className='app-icon d-flex align-items-center justify-content-between'>
                                                        <div className="d-flex align-items-center">
                                                            {groupIcon}
                                                            {!collapsed && <span className="ms-2">{groupName}</span>}
                                                        </div>
                                                        {!collapsed && (
                                                            <FontAwesomeIcon
                                                                icon={isExpanded ? faChevronDown : faChevronRight}
                                                                className="ms-auto"
                                                            />
                                                        )}
                                                    </div>
                                                </button>
                                            )}
                                            {collapsed && groupName && <UncontrolledTooltip placement="right" target={groupId}>{groupName}</UncontrolledTooltip>}
                                            {(!groupName || isExpanded) && apps.map((application, appIdx) => {
                                                const hasExpandedSubmenu = application.sidebarSubMenu && this.state.expandedItems[application.applicationFullName + ""];
                                                const isSelectedApp = this.state.selectedAppName === application.displayName && !hasExpandedSubmenu;
                                                const appId = `app-${index}-${appIdx}`;
                                                return (
                                                    <div key={application.applicationFullName + getUID()} className={groupName ? "ms-3" : ""}>
                                                        <button
                                                            id={appId}
                                                            className={
                                                                `btn app-link ${isSelectedApp ? 'active-link' : ''} ` +
                                                                (application.bottomSeparator ? 'bottomSeparator ' : '') +
                                                                (application.topSeparator ? 'topSeparator ' : '')
                                                            }
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (application.sidebarSubMenu) {
                                                                    this.toggleExpand(application.applicationFullName + "");
                                                                } else {
                                                                    this.onLaunch(e, application);
                                                                }
                                                            }}
                                                            title={collapsed ? application.displayName : undefined}
                                                        >
                                                            <div className="app-icon d-flex align-items-center justify-content-between">
                                                                <div className="d-flex align-items-center">
                                                                    {!groupName && application.icon && <div>{application.icon}</div>}
                                                                    {!collapsed && <span className="ms-2">{application.displayName}</span>}
                                                                </div>
                                                                {!collapsed && application.sidebarSubMenu && (
                                                                    <FontAwesomeIcon
                                                                        icon={this.state.expandedItems[application.applicationFullName + ""] ? faChevronDown : faChevronRight}
                                                                        className="ms-auto"
                                                                    />
                                                                )}
                                                            </div>
                                                        </button>
                                                        {collapsed && <UncontrolledTooltip placement="right" target={appId}>{application.displayName}</UncontrolledTooltip>}
                                                        {!collapsed && application.sidebarSubMenu && this.state.expandedItems[application.applicationFullName + ""] && (
                                                            <div className="app-shortcuts">
                                                                {React.cloneElement(
                                                                    application.sidebarSubMenu as React.ReactElement<any>,
                                                                    {
                                                                        selectedRoute: this.state.selectedSubmenuRoute,
                                                                        onSubmenuClick: (route: string) => this.setState({ selectedSubmenuRoute: route })
                                                                    }
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </section>
                </div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    console: state.console
});

const mapDispatchToPros = (dispatch: Dispatch) => ({
    push: (path: string) => dispatch(push(path)),
    consoleActions: bindActionCreators(ConsoleStore.actionCreators, dispatch)
});

export default connect(mapStateToProps, mapDispatchToPros)(withRouter(ConsoleSidebar));

